import { Capacitor } from '@capacitor/core';

const USE_PROXY = import.meta.env.VITE_USE_PROXY
  ? import.meta.env.VITE_USE_PROXY.toLowerCase() === 'true'
  : !Capacitor.isNativePlatform() && window.location.hostname === 'localhost';
const PROTO = USE_PROXY ? 'http:' : import.meta.env.VITE_API_PROTOCOL;
const API_HOST = USE_PROXY
  ? `${window.location.host}`
  : import.meta.env.VITE_API_TARGET.replace('{location.hostname}', window.location.hostname);
export const BASE_URL = `${PROTO}//${API_HOST}`;

/**
 * Retry a request that never got a response (network failure) with an
 * exponential backoff, then reject: the pollers call again on their next tick,
 * so a lasting outage must not turn into a continuous stream of requests.
 * A completed response, even a 4xx/5xx, is returned as is.
 */
export async function retriedFetch(
  url: string,
  options: { retryInterval?: number; maxRetries?: number; maxRetryInterval?: number } = {},
): Promise<Response> {
  const { retryInterval = 1000, maxRetries = 4, maxRetryInterval = 8000 } = options;
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetch(url);
    } catch (e) {
      if (attempt >= maxRetries) throw e;
      const delay = Math.min(retryInterval * 2 ** attempt, maxRetryInterval);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

/** A completed request with an error status, as opposed to a network failure. */
export function isHttpError(error: unknown): boolean {
  return error instanceof Error && /^\d{3}: /.test(error.message);
}

const inFlight: { [key: string]: Promise<Response> } = {};
/**
 * Ensure only one in-flight GET request for a given url.
 * return is : Promise is resolved with HTTP Response or rejected with error informations
 */
async function getResource(url: string): Promise<Response> {
  if (!inFlight.hasOwnProperty(url)) {
    inFlight[url] = retriedFetch(url);
    inFlight[url].then(
      () => {
        delete inFlight[url];
      },
      () => {
        delete inFlight[url];
      },
    );
  }

  return inFlight[url].then(response => {
    if (!response.ok) {
      throw new Error(`${response.status}: ${response.statusText} when trying to get ${response.url}`);
    }

    return response;
  });
}

/**
 * GET a JSON resource. Avoid response cloning on getResource.
 */
export async function getJson(url: string): Promise<any> {
  const response = await getResource(url);
  return response.clone().json();
}

/**
 * GET a JSON array and return an indexed version v3
 */
export async function getCollection(
  groupId: string,
  collectionPath: string,
  params: URLSearchParams | null = null,
  version: string = 'v3',
): Promise<any> {
  let request = `${BASE_URL}/api/${version}/groups/${groupId}/${collectionPath}`;

  if (params && params.toString() !== '') request += `?${params}`;

  return await getJson(request);
}
